import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Package, FileText, MapPin } from 'lucide-react';

const ReceiptDetailDialog = ({ open, onClose, receipt }) => {
  if (!receipt) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            Detalii Receptie {receipt.receipt_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Info */}
          <Card className="bg-blue-50">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-slate-600">Nr. Factura</p>
                  <p className="font-bold text-lg">{receipt.invoice_number}</p>
                </div>
                <div>
                  <p className="text-slate-600">Data Factura</p>
                  <p className="font-semibold">{new Date(receipt.invoice_date).toLocaleDateString('ro-RO')}</p>
                </div>
                <div>
                  <p className="text-slate-600">Furnizor</p>
                  <p className="font-semibold">{receipt.supplier_name}</p>
                </div>
                <div>
                  <p className="text-slate-600">Sediu</p>
                  <p className="font-semibold">{receipt.plant_name}</p>
                </div>
                <div>
                  <p className="text-slate-600">Primit de</p>
                  <p className="font-semibold">{receipt.received_by_name}</p>
                </div>
                <div>
                  <p className="text-slate-600">Data Primire</p>
                  <p className="font-semibold">{new Date(receipt.created_at).toLocaleDateString('ro-RO')}</p>
                </div>
                <div>
                  <p className="text-slate-600">Valoare Totala</p>
                  <p className="font-bold text-xl text-green-600">{receipt.total_invoice_value.toFixed(2)} {receipt.currency}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <div>
            <h3 className="text-lg font-bold mb-3">Articole Primite ({receipt.items_count})</h3>
            <div className="space-y-2">
              {receipt.items?.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-bold text-lg">{item.article_name}</p>
                          <Badge variant="outline">{item.article_code}</Badge>
                          {item.qc_status === 'damaged' && <Badge className="bg-red-600">Damaged</Badge>}
                          {item.qc_status === 'to_inspect' && <Badge className="bg-orange-500">To Inspect</Badge>}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500">Cantitate</p>
                            <p className="font-semibold">{item.quantity} {item.unit}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Pret Unitar</p>
                            <p className="font-semibold">{item.unit_price.toFixed(2)} {receipt.currency}</p>
                          </div>
                          {item.discount_percent > 0 && (
                            <div>
                              <p className="text-slate-500">Discount</p>
                              <p className="font-semibold text-orange-600">{item.discount_percent}%</p>
                            </div>
                          )}
                          <div>
                            <p className="text-slate-500">Total</p>
                            <p className="font-semibold text-green-600">{item.total_price.toFixed(2)} {receipt.currency}</p>
                          </div>
                        </div>
                        {item.lot_number && (
                          <p className="text-xs text-slate-600 mt-2">LOT: {item.lot_number}</p>
                        )}
                        {item.expiry_date && (
                          <p className="text-xs text-slate-600">Expirare: {new Date(item.expiry_date).toLocaleDateString('ro-RO')}</p>
                        )}
                        
                        {/* BIN Allocations */}
                        {item.bin_allocations?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-600 mb-1">Alocare Locatii:</p>
                            <div className="flex flex-wrap gap-1">
                              {item.bin_allocations.map((alloc, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {alloc.cabinet}→{alloc.shelf}→{alloc.position}: {alloc.quantity}{item.unit}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {receipt.notes && (
            <Card className="bg-slate-50">
              <CardContent className="p-4">
                <p className="text-sm text-slate-600 mb-1">Note:</p>
                <p className="text-slate-900">{receipt.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptDetailDialog;
